import { foldSearch, type Microsoft365AdminDto, type Microsoft365Availability } from '@dbrain/shared';
import { Button, Dialog, EmptyState, RadioCard, TextInput } from '@dbrain/ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMicrosoft365Settings } from '../../../api/microsoft365-admin.js';
import { ErrorAlert } from '../../ErrorAlert.js';
import { allowedTeams, audienceChanged } from './model.js';

/** past this many teams the list gets a search box */
const SEARCH_FROM = 8;

/**
 * "Kim kullanabilir · Düzenle": everyone (team admins may still turn it off for their teams) or only the chosen
 * teams. Saved in one change; nothing changed, nothing is sent (so no empty audit row).
 */
export function AudienceDialog({ dto, onClose }: { dto: Microsoft365AdminDto; onClose: () => void }) {
  const { t } = useTranslation();
  const listId = useId();
  const save = useMicrosoft365Settings();
  const [availability, setAvailability] = useState<Microsoft365Availability>(dto.availability);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(allowedTeams(dto.teams).map((team) => team.id)));
  const [q, setQ] = useState('');
  const needle = foldSearch(q.trim());
  const shown = needle ? dto.teams.filter((team) => foldSearch(team.name).includes(needle)) : dto.teams;
  const empty = availability === 'teams' && chosen.size === 0;
  const toggle = (id: string, on: boolean) =>
    setChosen((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const submit = () => {
    if (!audienceChanged(dto, availability, chosen)) return onClose();
    save.mutate(availability === 'everyone' ? { availability } : { availability, teamIds: [...chosen] }, { onSuccess: onClose });
  };
  return (
    <Dialog
      title={t('microsoft365.admin.audienceDialog.title')}
      onClose={onClose}
      closeLabel={t('common.close')}
      data-testid="m365-audience-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            {t('common.cancel')}
          </Button>
          <Button busy={save.isPending} disabled={empty} onClick={submit} data-testid="m365-audience-save">
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="m365a-audience" role="radiogroup" aria-label={t('microsoft365.admin.summary.audience.title')}>
        <RadioCard
          name="m365-availability"
          value="everyone"
          checked={availability === 'everyone'}
          onChange={() => setAvailability('everyone')}
          title={t('microsoft365.admin.summary.audience.everyone')}
          hint={t('microsoft365.admin.audienceDialog.everyoneHint')}
          data-testid="m365-audience-everyone"
        />
        <RadioCard
          name="m365-availability"
          value="teams"
          checked={availability === 'teams'}
          onChange={() => setAvailability('teams')}
          title={t('microsoft365.admin.audienceDialog.teams')}
          hint={t('microsoft365.admin.audienceDialog.teamsHint')}
          data-testid="m365-audience-teams"
        />
      </div>
      {availability === 'teams' ? (
        <fieldset className="m365a-audience__teams" aria-describedby={empty ? `${listId}-empty` : undefined}>
          <legend className="ui-visually-hidden">{t('microsoft365.admin.audienceDialog.teamsLabel')}</legend>
          {dto.teams.length > SEARCH_FROM ? (
            <TextInput
              type="search"
              className="ui-input--sm m365a-audience__search"
              value={q}
              placeholder={t('microsoft365.admin.audienceDialog.search')}
              aria-label={t('microsoft365.admin.audienceDialog.search')}
              onChange={(e) => setQ(e.target.value)}
              data-testid="m365-audience-search"
            />
          ) : null}
          {dto.teams.length === 0 ? <EmptyState>{t('microsoft365.admin.audienceDialog.none')}</EmptyState> : null}
          {dto.teams.length > 0 && shown.length === 0 ? <EmptyState>{t('microsoft365.admin.audienceDialog.nothingFound')}</EmptyState> : null}
          <ul className="m365a-audience__list">
            {shown.map((team) => (
              <li key={team.id}>
                <label className="m365a-audience__team" data-testid="m365-audience-team" data-name={team.name}>
                  <input type="checkbox" checked={chosen.has(team.id)} onChange={(e) => toggle(team.id, e.target.checked)} />
                  <span className="m365a-audience__name">{team.name}</span>
                  <span className="m365a-audience__meta">
                    {t('microsoft365.admin.audienceDialog.members', { count: team.members })}
                    {team.teamEnabled ? '' : ` · ${t('microsoft365.admin.audienceDialog.turnedOff')}`}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {empty ? (
            <p id={`${listId}-empty`} className="ui-field__hint" data-testid="m365-audience-pick">
              {t('microsoft365.admin.audienceDialog.pickOne')}
            </p>
          ) : null}
        </fieldset>
      ) : null}
      <ErrorAlert error={save.error} />
    </Dialog>
  );
}
